import { useEffect, useRef, useState } from 'react';
export default function useTasteMotion({ duration = 1200, deferred = false, enabled = true } = {}) {
  const ref = useRef(null),
    [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!enabled) {
      setProgress(1);
      return;
    }
    let frame,
      observer,
      started = false,
      disposed = false;
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const complete = () => {
      cancelAnimationFrame(frame);
      if (!disposed) setProgress(1);
    };
    const start = () => {
      if (started || disposed) return;
      started = true;
      if (media?.matches || document.hidden) {
        complete();
        return;
      }
      let origin;
      const tick = (time) => {
        if (disposed) return;
        origin ??= time;
        const elapsed = Math.min(1, (time - origin) / duration);
        setProgress(elapsed);
        if (elapsed < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };
    const visibility = () => {
      if (document.hidden && started) complete();
    };
    const preference = () => {
      if (media.matches) {
        started = true;
        complete();
      }
    };
    if (deferred && typeof IntersectionObserver !== 'undefined' && ref.current && !media?.matches) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            start();
            observer.disconnect();
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(ref.current);
    } else start();
    document.addEventListener('visibilitychange', visibility);
    media?.addEventListener?.('change', preference);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();
      document.removeEventListener('visibilitychange', visibility);
      media?.removeEventListener?.('change', preference);
    };
  }, [duration, deferred, enabled]);
  return [ref, progress];
}
